import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  DateInput,
  Field,
  Input,
  Button,
} from '@truecare/ui';
import type {
  AdminCaregiverDetailResponse,
  AdminCaregiverStatusResponse,
  AdminCaregiverPatch,
  AdminCredentialDetail,
  AdminComplianceArtifactDetail,
  AdminBankingVerifyResponse,
  AdminBankingRejectResponse,
  AdminCaregiverBankingResponse,
  CaregiverProfilePhotoResponse,
  CaregiverResumeResponse,
  WorkExperienceListResponse,
  AvailabilityResponse,
  AvailabilityListResponse,
} from '@truecare/schemas';
import {
  CREDENTIAL_LABELS,
  COMPLIANCE_LABELS,
  genderLabel,
  skillsForRoles,
  IMMUNIZATION_CATALOG,
} from '@truecare/schemas';
import { ApiError, TotpStepUpRequiredError, messageOf, unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { actionErrorMessage } from '../../lib/action-error';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import { Avatar } from '../../components/Avatar';
import { AuditTrailPanel } from '../../components/AuditTrailPanel';
import { PayoutsCard } from '../../components/PayoutsCard';
import { ShiftsCard } from '../../components/ShiftsCard';
import { AgreementAcceptancesCard } from '../../components/AgreementAcceptancesCard';
import { DangerButton } from '../../components/DangerButton';
import { OverrideActionButton } from '../../components/OverrideActionButton';

/**
 * Phase 14b-2 — Caregiver detail page (largest persona surface).
 *
 * Action cards per concern per 14b-2-3:
 *   1. Identity (read-only)
 *   2. Status (approve / reject / suspend / unsuspend / terminate — approve moves
 *      UNDER_REVIEW → ONBOARDING; ACTIVE is auto-reached, no manual activate)
 *   3. Credentials (approve / reject each — NACC / CNO / CPR / DSW / Food Handler / VSC / Gov ID)
 *   4. Compliance artifacts (approve / reject each — TB / GL insurance; WSIB + vaccination retired from vetting)
 *   5. Banking & payout (verify / reject; flip BIWEEKLY ↔ WEEKLY)
 *   6. AuditTrailPanel at bottom per 14b-6
 *
 * All destructive mutations route through TanStack Query's MutationCache
 * (wired in main.tsx) so a TotpStepUpRequiredError opens the global
 * modal + retries the failing mutation per 14b-1-5.
 */

export const Route = createFileRoute('/caregivers/$id')({
  beforeLoad: ({ params }) => adminBeforeLoad(`/caregivers/${params.id}`),
  component: CaregiverDetailPage,
});

function CaregiverDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailKey = ['admin', 'caregivers', id] as const;
  const detail = useQuery<AdminCaregiverDetailResponse>({
    queryKey: detailKey,
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}', {
          params: { path: { id } },
        } as never),
      )) as unknown as AdminCaregiverDetailResponse,
  });

  // Phase 17 — read-only profile photo for the identity header.
  const photo = useQuery<CaregiverProfilePhotoResponse>({
    queryKey: ['admin', 'caregivers', id, 'profile-photo'] as const,
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}/profile-photo', {
          params: { path: { id } },
        } as never),
      )) as unknown as CaregiverProfilePhotoResponse,
  });

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: detailKey });
  }

  // Phase 5g — operator approves vetting (UNDER_REVIEW → ONBOARDING). Replaces
  // the old /activate; the care professional then completes banking + SIN +
  // contract and auto-activates (there is no manual "activate" button anymore).
  const approve = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/approve', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminCaregiverStatusResponse,
    onSuccess: invalidate,
  });

  // Audit H8 — reject application (UNDER_REVIEW → APPLYING) so the caregiver
  // can re-open their portal credential/compliance sections and self-serve.
  const rejectApplication = useMutation({
    mutationFn: async (notes: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/reject', {
          params: { path: { id } },
          body: { notes },
        } as never),
      )) as unknown as AdminCaregiverStatusResponse,
    onSuccess: invalidate,
  });

  // Phase 14b-2 post-audit H1 — caregiver unsuspend (SUSPENDED → ACTIVE)
  // distinct from `/activate` (which is the first-time vetting-complete
  // path). Without this, the SUSPENDED-status UI had to click Activate,
  // which 409s server-side.
  const unsuspend = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/unsuspend', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminCaregiverStatusResponse,
    onSuccess: invalidate,
  });

  const suspend = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/suspend', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminCaregiverStatusResponse,
    onSuccess: invalidate,
  });

  const terminate = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/terminate', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminCaregiverStatusResponse,
    // Bug #10 (parity with facility terminate) — terminal, irreversible state →
    // return to the list (row now shows TERMINATED) instead of sitting on a
    // dead detail page. Prefix invalidation catches both this detail + the list.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers'] });
      void navigate({ to: '/caregivers' as string });
    },
  });

  const approveCredential = useMutation({
    mutationFn: async (input: { credId: string; notes?: string }) =>
      (await unwrap(
        api.POST(
          '/api/v1/admin/caregivers/{id}/credentials/{credId}/approve',
          {
            params: { path: { id, credId: input.credId } },
            body: { notes: input.notes ?? null },
          } as never,
        ),
      )) as unknown,
    onSuccess: invalidate,
  });

  const rejectCredential = useMutation({
    mutationFn: async (input: { credId: string; notes: string }) =>
      (await unwrap(
        api.POST(
          '/api/v1/admin/caregivers/{id}/credentials/{credId}/reject',
          {
            params: { path: { id, credId: input.credId } },
            body: { notes: input.notes },
          } as never,
        ),
      )) as unknown,
    onSuccess: invalidate,
  });

  const approveCompliance = useMutation({
    mutationFn: async (input: { artifactId: string; notes?: string }) =>
      (await unwrap(
        api.POST(
          '/api/v1/admin/caregivers/{id}/compliance/{artifactId}/approve',
          {
            params: { path: { id, artifactId: input.artifactId } },
            body: { notes: input.notes ?? null },
          } as never,
        ),
      )) as unknown,
    onSuccess: invalidate,
  });

  const rejectCompliance = useMutation({
    mutationFn: async (input: { artifactId: string; notes: string }) =>
      (await unwrap(
        api.POST(
          '/api/v1/admin/caregivers/{id}/compliance/{artifactId}/reject',
          {
            params: { path: { id, artifactId: input.artifactId } },
            body: { notes: input.notes },
          } as never,
        ),
      )) as unknown,
    onSuccess: invalidate,
  });

  const verifyBanking = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/banking/verify', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminBankingVerifyResponse | AdminBankingRejectResponse,
    onSuccess: invalidate,
  });

  const rejectBanking = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/banking/reject', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminBankingVerifyResponse | AdminBankingRejectResponse,
    onSuccess: invalidate,
  });

  // Audit H4 — the void-cheque signed URL is no longer on the (plain, non-fresh)
  // detail response. Fetch it on demand from the TotpFresh-guarded /banking GET.
  // Modeling it as a MUTATION routes a TotpStepUpRequiredError through the global
  // MutationCache→step-up bridge (main.tsx), so clicking "View void cheque" pops
  // the 2FA step-up when the session isn't fresh, then returns the signed URL.
  const revealBanking = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}/banking', {
          params: { path: { id } },
        } as never),
      )) as unknown as AdminCaregiverBankingResponse,
  });

  // 14b-2 post-audit-round-2 finding — request body shape MUST be
  // `{ frequency, reason }` per the Phase 11-2 lock; the operator-typed
  // reason is required (min 10 chars). The prior shape `{ payoutFrequency }`
  // would 400 every time. NOTE: TypeScript's openapi-fetch typing does
  // not enforce request-body shape strictly (verified by a minimal
  // typecheck repro on 2026-06-22); the openapi-typescript-generated
  // types declare the body but openapi-fetch's `Client<paths>` accepts
  // any object. This means body-shape bugs slip past `pnpm typecheck`
  // — backend integration tests + Phase 14b-3 Playwright are the
  // structural defense. Keep the body literal aligned with the Zod
  // schema in packages/schemas/src/caregiver/banking-admin.schema.ts.
  const setPayoutFrequency = useMutation({
    mutationFn: async (input: {
      frequency: 'BIWEEKLY' | 'WEEKLY';
      reason: string;
    }) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/payout-frequency', {
          params: { path: { id } },
          body: { frequency: input.frequency, reason: input.reason },
        } as never),
      )) as unknown,
    onSuccess: invalidate,
  });

  if (detail.isPending) {
    return (
      <AdminLayout>
        <p className="text-sm text-ink-muted">Loading care professional…</p>
      </AdminLayout>
    );
  }

  if (detail.isError) {
    const status = detail.error instanceof ApiError ? detail.error.status : 0;
    return (
      <AdminLayout>
        <p className="text-sm text-danger">
          Failed to load care professional {status ? `(${status})` : ''}.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-4"
          onClick={() => navigate({ to: '/caregivers' as string })}
        >
          Back to list
        </Button>
      </AdminLayout>
    );
  }

  const c = detail.data;
  const displayName = c.preferredName ?? c.legalGivenName;

  return (
    <AdminLayout>
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-4">
          <Avatar
            photoUrl={photo.data?.url ?? null}
            name={`${displayName} ${c.legalSurname}`}
            size={56}
          />
          <div>
            <h1 className="font-heading text-3xl text-forest md:text-4xl">
              {displayName} {c.legalSurname}
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              {c.email}
              {c.roles.length > 0 && (
                <>
                  {' '}
                  · {c.roles.map((r) => r.code).join(', ')}
                </>
              )}
            </p>
          </div>
        </div>
        <StatusBadge status={c.status} />
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() => navigate({ to: '/caregivers' as string })}
      >
        ← Back to list
      </Button>

      <IdentityCard data={c} />

      <SkillsCard data={c} />

      <PayOverrideCard data={c} />

      <ImmunizationsCard data={c} />

      <StatusActionsCard
        status={c.status}
        suspendedReason={c.suspensionReason}
        onApprove={() => approve.mutateAsync()}
        onUnsuspend={() => unsuspend.mutateAsync()}
        onSuspend={(reason) => suspend.mutateAsync(reason)}
        onTerminate={(reason) => terminate.mutateAsync(reason)}
        onReject={(notes) => rejectApplication.mutateAsync(notes)}
        isApprovePending={approve.isPending}
        isUnsuspendPending={unsuspend.isPending}
        isSuspendPending={suspend.isPending}
        isTerminatePending={terminate.isPending}
        isRejectPending={rejectApplication.isPending}
      />

      <CredentialsCard
        credentials={c.credentials}
        onApprove={(credId) => approveCredential.mutateAsync({ credId })}
        onReject={(credId, notes) =>
          rejectCredential.mutateAsync({ credId, notes })
        }
        isApprovePending={approveCredential.isPending}
        isRejectPending={rejectCredential.isPending}
      />

      <ComplianceCard
        artifacts={c.complianceArtifacts}
        onApprove={(artifactId) => approveCompliance.mutateAsync({ artifactId })}
        onReject={(artifactId, notes) =>
          rejectCompliance.mutateAsync({ artifactId, notes })
        }
        isApprovePending={approveCompliance.isPending}
        isRejectPending={rejectCompliance.isPending}
      />

      {/* Doc 16 — optional self-reported certificates (no review step). */}
      <AdditionalCertificatesCard data={c} />

      {/* Phase 17 (change-list 1.2) — vetting aid. Read-only: the professional
          owns these rows. Buyers only ever see the derived total. */}
      <WorkExperienceCard caregiverId={c.id} />

      {/* Operator visibility into WHEN the caregiver is available (the same rows
          matching reads). Read-only — availability is caregiver-owned (Principle
          9); there is no operator edit path. Sits next to Shifts (scheduling). */}
      <AvailabilityCard caregiverId={c.id} />

      <ShiftsCard persona="caregiver" entityId={c.id} />

      <PayoutsCard caregiverId={c.id} />

      <BankingCard
        eftOnFile={c.eftOnFile}
        eftVerified={c.eftVerified}
        eftRejected={c.eftRejected}
        eftRejectionReason={c.eftRejectionReason}
        bankingAccountLast4={c.bankingAccountLast4}
        taxEntityType={c.taxEntityType}
        sinOnFile={c.sinOnFile}
        businessNumber={c.businessNumber}
        hstRegistered={c.hstRegistered}
        hstNumber={c.hstNumber}
        onVerify={() => verifyBanking.mutateAsync()}
        onReject={(reason) => rejectBanking.mutateAsync(reason)}
        onRevealVoidCheque={async () =>
          (await revealBanking.mutateAsync()).voidChequeDownloadUrl
        }
        onSetPayoutFrequency={(input) => setPayoutFrequency.mutateAsync(input)}
        isVerifyPending={verifyBanking.isPending}
        isRejectPending={rejectBanking.isPending}
        isRevealPending={revealBanking.isPending}
        isFrequencyPending={setPayoutFrequency.isPending}
      />

      {/* Phase 23 (23f) — signed IC agreement(s): the accepted versions +
          legal evidence + signed PDFs. Keyed on the User id, not caregiver id. */}
      <AgreementAcceptancesCard userId={c.userId} />

      <Card padding="md" className="mt-6" data-testid="caregiver-audit-card">
        <CardHeader title="Audit trail" />
        <AuditTrailPanel
          mode="entity-scoped"
          filters={{
            entityType: 'caregiver',
            entityId: c.id,
            page: 1,
            limit: 25,
          }}
        />
      </Card>
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  testId = 'caregiver-status-badge',
}: {
  status: string;
  testId?: string;
}) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-forest/10 text-forest'
      : // Phase 5g — ONBOARDING is an approved, in-pipeline state; group it with
        // the other pre-active states so it never falls through to the danger
        // (red) branch that reads as TERMINATED/REJECTED.
        status === 'APPLYING' ||
          status === 'UNDER_REVIEW' ||
          status === 'ONBOARDING'
        ? 'bg-cream text-ink-muted border border-border'
        : status === 'SUSPENDED'
          ? 'bg-gold/10 text-gold'
          : 'bg-danger/10 text-danger';
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${tone}`}
      data-testid={testId}
    >
      {status.replaceAll('_', ' ')}
    </span>
  );
}

// Canadian provinces/territories (the CanadianProvinceCode schema validates a
// 2-letter code; this is the display set for the picker).
const PROVINCE_OPTIONS = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
] as const;

interface ProfileForm {
  legalGivenName: string;
  legalSurname: string;
  legalMiddleNames: string;
  preferredName: string;
  dateOfBirth: string;
  gender: string;
  phoneMobile: string;
  phoneAlternate: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  operatorNotes: string;
  maxTravelKm: string; // form input; parsed to number in patchBody
}

function formFromData(d: AdminCaregiverDetailResponse): ProfileForm {
  return {
    legalGivenName: d.legalGivenName ?? '',
    legalSurname: d.legalSurname ?? '',
    legalMiddleNames: d.legalMiddleNames ?? '',
    preferredName: d.preferredName ?? '',
    dateOfBirth: d.dateOfBirth ?? '',
    gender: d.gender ?? '',
    phoneMobile: d.phoneMobile ?? '',
    phoneAlternate: d.phoneAlternate ?? '',
    addressLine1: d.addressLine1 ?? '',
    addressLine2: d.addressLine2 ?? '',
    city: d.city ?? '',
    province: d.province ?? '',
    postalCode: d.postalCode ?? '',
    operatorNotes: d.operatorNotes ?? '',
    maxTravelKm: String(d.maxTravelKm),
  };
}

/** Trim; empty string → null (nullable fields must send null, not ""). */
function nn(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

/**
 * Identity card — read-only view + operator "Edit profile" form.
 *
 * PATCH /admin/caregivers/:id is TotpFresh-guarded; this uses a plain
 * useMutation (no per-form step-up code). A TotpStepUpRequiredError is caught +
 * replayed by the global MutationCache→TotpStepUpModalProvider bridge (main.tsx),
 * so onSuccess (invalidate + close edit) fires on the replayed success too. We
 * only surface non-step-up errors inline (e.g. 422 on a bad postal/phone).
 */
function IdentityCard({ data }: { data: AdminCaregiverDetailResponse }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileForm>(() => formFromData(data));

  const patch = useMutation({
    mutationFn: async (body: AdminCaregiverPatch) =>
      (await unwrap(
        api.PATCH('/api/v1/admin/caregivers/{id}', {
          params: { path: { id: data.id } },
          body,
        } as never),
      )) as unknown,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'caregivers', data.id],
      });
      setEditing(false);
    },
  });

  function startEdit(): void {
    setForm(formFromData(data));
    patch.reset();
    setEditing(true);
  }

  function set<K extends keyof ProfileForm>(key: K, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function save(): void {
    const travel = Number(form.maxTravelKm);
    patch.mutate({
      legalGivenName: form.legalGivenName.trim(),
      legalSurname: form.legalSurname.trim(),
      legalMiddleNames: nn(form.legalMiddleNames),
      preferredName: nn(form.preferredName),
      dateOfBirth: nn(form.dateOfBirth),
      gender: nn(form.gender) as
        | 'FEMALE'
        | 'MALE'
        | 'NON_BINARY'
        | 'PREFER_NOT_TO_SAY'
        | null,
      phoneMobile: nn(form.phoneMobile),
      phoneAlternate: nn(form.phoneAlternate),
      addressLine1: nn(form.addressLine1),
      addressLine2: nn(form.addressLine2),
      city: nn(form.city),
      province: nn(form.province),
      postalCode: nn(form.postalCode),
      operatorNotes: nn(form.operatorNotes),
      // Backstop for the caregiver's travel radius; send only a valid 1–150 int.
      ...(Number.isInteger(travel) && travel >= 1 && travel <= 150
        ? { maxTravelKm: travel }
        : {}),
    });
  }

  // Travel radius must be a whole 1–150 (matches MaxTravelKmSchema). Validate
  // client-side + block Save so an out-of-range entry can't be SILENTLY dropped
  // from the patch (leaving the operator with a false "saved" on a value that
  // governs who gets offered work).
  const travelNum = Number(form.maxTravelKm);
  const maxTravelKmValid =
    form.maxTravelKm.trim() !== '' &&
    Number.isInteger(travelNum) &&
    travelNum >= 1 &&
    travelNum <= 150;

  // Legal name parts are required (min 1) — the caregiver's name must match ID.
  const canSave =
    form.legalGivenName.trim().length > 0 &&
    form.legalSurname.trim().length > 0 &&
    maxTravelKmValid;

  // Step-up errors replay globally — never surface them inline.
  const showError =
    patch.isError && !(patch.error instanceof TotpStepUpRequiredError);
  const errorText = ((): string => {
    const e = patch.error;
    if (e instanceof ApiError) {
      if (e.status === 422) {
        return 'Some fields are invalid — check phone (10–30 digits) and postal code (e.g. M5V 2T6).';
      }
      return `Save failed (${e.status}).`;
    }
    return 'Save failed. Please try again.';
  })();

  const fullAddress =
    [data.addressLine1, data.addressLine2, data.city, data.province, data.postalCode]
      .filter((p): p is string => Boolean(p))
      .join(', ') || null;

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-identity-card">
      <CardHeader
        title="Identity"
        action={
          !editing ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={startEdit}
              data-testid="caregiver-edit-profile-button"
            >
              Edit profile
            </Button>
          ) : undefined
        }
      />

      {!editing ? (
        <>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Row label="Legal given name" value={data.legalGivenName} />
            <Row label="Legal surname" value={data.legalSurname} />
            <Row label="Legal middle names" value={data.legalMiddleNames} />
            <Row label="Preferred name" value={data.preferredName} />
            <Row label="Date of birth" value={data.dateOfBirth} />
            <Row label="Gender" value={genderLabel(data.gender)} />
            <Row label="Email" value={data.email} />
            <Row label="Phone (mobile)" value={data.phoneMobile} />
            <Row label="Phone (alt)" value={data.phoneAlternate} />
            <Row label="Address" value={fullAddress} />
            {/* Read-only so the operator sees the current travel radius without
                entering edit mode (it governs matching + the picker range badge).
                Editable via "Edit profile" — operator backstop. */}
            <Row label="Max travel radius" value={`${String(data.maxTravelKm)} km`} />
            <Row
              label="Applied"
              value={
                data.applicationSubmittedAt
                  ? new Date(data.applicationSubmittedAt).toLocaleString('en-CA')
                  : null
              }
            />
            <Row
              label="Activated"
              value={
                data.activatedAt
                  ? new Date(data.activatedAt).toLocaleString('en-CA')
                  : null
              }
            />
            {/* Optional apply-time "Referred by" free-text (read-only). */}
            <Row label="Referred by" value={data.referralSource} />
          </dl>
          {data.operatorNotes && (
            <div className="mt-4 rounded-md border border-border bg-cream/50 px-3 py-2">
              <p className="text-xs uppercase tracking-wide text-ink-muted">
                Operator notes
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-ink">
                {data.operatorNotes}
              </p>
            </div>
          )}
        </>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save();
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Legal given name (required)">
              <Input
                value={form.legalGivenName}
                onChange={(e) => set('legalGivenName', e.target.value)}
                maxLength={120}
                data-testid="edit-legal-given-name"
              />
            </Field>
            <Field label="Legal surname (required)">
              <Input
                value={form.legalSurname}
                onChange={(e) => set('legalSurname', e.target.value)}
                maxLength={120}
                data-testid="edit-legal-surname"
              />
            </Field>
            <Field label="Legal middle names">
              <Input
                value={form.legalMiddleNames}
                onChange={(e) => set('legalMiddleNames', e.target.value)}
                maxLength={240}
              />
            </Field>
            <Field label="Preferred name">
              <Input
                value={form.preferredName}
                onChange={(e) => set('preferredName', e.target.value)}
                maxLength={120}
              />
            </Field>
            <Field label="Date of birth">
              <DateInput
                value={form.dateOfBirth}
                onChange={(v) => set('dateOfBirth', v)}
                data-testid="edit-dob"
              />
            </Field>
            <Field label="Gender">
              <select
                className="h-10 w-full rounded-md border border-border bg-white px-2 text-sm text-forest"
                value={form.gender}
                onChange={(e) => set('gender', e.target.value)}
                data-testid="edit-gender"
              >
                <option value="">— Not set —</option>
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
                <option value="NON_BINARY">Non-binary</option>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
              </select>
            </Field>
            <Field label="Phone (mobile)">
              <Input
                value={form.phoneMobile}
                onChange={(e) => set('phoneMobile', e.target.value)}
                data-testid="edit-phone-mobile"
              />
            </Field>
            <Field label="Phone (alt)">
              <Input
                value={form.phoneAlternate}
                onChange={(e) => set('phoneAlternate', e.target.value)}
              />
            </Field>
            <Field label="Address line 1">
              <Input
                value={form.addressLine1}
                onChange={(e) => set('addressLine1', e.target.value)}
                maxLength={200}
                data-testid="edit-address-line1"
              />
            </Field>
            <Field label="Address line 2">
              <Input
                value={form.addressLine2}
                onChange={(e) => set('addressLine2', e.target.value)}
                maxLength={200}
              />
            </Field>
            <Field label="City">
              <Input
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                maxLength={120}
              />
            </Field>
            <Field label="Province">
              <select
                value={form.province}
                onChange={(e) => set('province', e.target.value)}
                className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                data-testid="edit-province"
              >
                <option value="">—</option>
                {PROVINCE_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Postal code">
              <Input
                value={form.postalCode}
                onChange={(e) => set('postalCode', e.target.value)}
                data-testid="edit-postal-code"
              />
            </Field>
          </div>

          <Field
            label="Max travel radius (km, 1–150) — professional-declared; operator backstop"
            error={maxTravelKmValid ? undefined : 'Enter a whole number from 1 to 150.'}
          >
            <Input
              type="number"
              min={1}
              max={150}
              step={1}
              className="md:max-w-[10rem]"
              value={form.maxTravelKm}
              onChange={(e) => set('maxTravelKm', e.target.value)}
              data-testid="edit-max-travel-km"
            />
          </Field>

          <Field label="Operator notes (internal — not visible to the professional)">
            <textarea
              value={form.operatorNotes}
              onChange={(e) => set('operatorNotes', e.target.value)}
              rows={3}
              maxLength={10000}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="edit-operator-notes"
            />
          </Field>

          {showError && (
            <p className="text-sm text-danger" data-testid="caregiver-edit-error">
              {errorText}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              disabled={!canSave}
              loading={patch.isPending}
              data-testid="caregiver-edit-save"
            >
              Save changes
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-ink">
        {value ?? <span className="text-ink-muted">—</span>}
      </dd>
    </div>
  );
}

// Operator view+edit of a caregiver's declared skills (footcare / palliative
// care, role-scoped). Full-set / idempotent POST; the write is TotpFresh-guarded,
// so the first toggle opens the global step-up modal, then the 15-min window
// keeps subsequent toggles smooth. Only role-eligible skills are shown, matching
// the shared SKILL_CATALOG the caregiver's own profile renders from.
function PayOverrideCard({ data }: { data: AdminCaregiverDetailResponse }) {
  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-pay-override-card">
      <CardHeader
        title="Pay-rate override"
        description="Custom hourly PAY rate per role, overriding the rate-card default. Effective now — applies to shifts accepted from now on; already-booked shifts keep their rate. Modifiers (night/weekend/stat) still apply on top. Applies to this role in ALL settings (LTC and homecare). Editing requires a fresh 2FA step-up."
      />
      {data.roles.length === 0 ? (
        <p className="text-sm text-ink-muted">This caregiver holds no roles.</p>
      ) : (
        <div className="mt-2 flex flex-col gap-4">
          {data.roles.map((r) => (
            <PayOverrideRow
              key={r.code}
              caregiverId={data.id}
              roleCode={r.code}
              current={r.payRateOverride}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function PayOverrideRow({
  caregiverId,
  roleCode,
  current,
}: {
  caregiverId: string;
  roleCode: string;
  current: number | null;
}) {
  const queryClient = useQueryClient();
  const [rate, setRate] = useState(current != null ? String(current) : '');
  const [reason, setReason] = useState('');
  // Set by the server's ±50% soft-confirm gate — holds the standard band so we
  // can prompt "$X is well outside $lo–$hi — set anyway?".
  const [needsConfirm, setNeedsConfirm] = useState<{ low: number; high: number } | null>(null);

  const mutation = useMutation({
    mutationFn: async (vars: { payRateOverride: number | null; confirmed: boolean }) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/pay-rate-override', {
          params: { path: { id: caregiverId } },
          body: {
            roleCode,
            payRateOverride: vars.payRateOverride,
            reason,
            confirmedOutOfBand: vars.confirmed,
          },
        } as never),
      )) as unknown,
    onSuccess: () => {
      setReason('');
      setNeedsConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers', caregiverId] });
    },
    onError: (err) => {
      // The ±50% soft gate returns 409 with the standard band → prompt to confirm.
      // Anything else (incl. the hard-block 422 "absurd") falls through to the
      // inline error message below.
      if (err instanceof ApiError) {
        const body = err.body as
          | { reason?: string; standardLow?: number; standardHigh?: number }
          | undefined;
        if (body?.reason === 'pay_rate_override_needs_confirmation') {
          setNeedsConfirm({ low: body.standardLow ?? 0, high: body.standardHigh ?? 0 });
          return;
        }
      }
      setNeedsConfirm(null);
    },
  });

  const parsed = Number(rate);
  // Match the backend schema bounds (min $0.01, max $10,000) so Save doesn't
  // enable for values the API will 400 on.
  const rateValid = rate.trim() !== '' && Number.isFinite(parsed) && parsed >= 0.01 && parsed <= 10_000;
  const reasonValid = reason.trim().length >= 10;
  const roundedRate = Number(parsed.toFixed(2));
  // A confirm prompt is showing (not an error); real errors are anything else.
  const showError =
    mutation.isError &&
    !needsConfirm &&
    !(mutation.error instanceof TotpStepUpRequiredError);
  const errorText =
    (mutation.error instanceof ApiError ? messageOf(mutation.error) : null) ??
    'Couldn’t save the pay rate. Check the amount + reason and try again.';

  return (
    <div className="rounded-md border border-border p-3" data-testid={`pay-override-row-${roleCode}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-forest">{roleCode}</span>
        <span className="text-sm text-ink-muted" data-testid={`pay-override-current-${roleCode}`}>
          {current != null ? `Override: $${current.toFixed(2)}/hr` : 'Rate-card default'}
        </span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        <Field label="New hourly pay rate ($)">
          <Input
            type="number"
            step="0.01"
            min="0"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            data-testid={`pay-override-rate-${roleCode}`}
          />
        </Field>
        <Field label="Reason (min 10 characters)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            data-testid={`pay-override-reason-${roleCode}`}
          />
        </Field>
        <div className="flex gap-2">
          <Button
            disabled={!rateValid || !reasonValid || mutation.isPending}
            onClick={() => {
              setNeedsConfirm(null);
              mutation.mutate({ payRateOverride: roundedRate, confirmed: false });
            }}
            data-testid={`pay-override-save-${roleCode}`}
          >
            Save override
          </Button>
          {current != null && (
            <Button
              variant="secondary"
              disabled={!reasonValid || mutation.isPending}
              onClick={() => mutation.mutate({ payRateOverride: null, confirmed: false })}
              data-testid={`pay-override-clear-${roleCode}`}
            >
              Clear (use default)
            </Button>
          )}
        </div>
      </div>
      {needsConfirm && (
        <div
          className="mt-2 rounded-md border border-warning/40 bg-warning/10 p-2 text-sm"
          data-testid={`pay-override-confirm-${roleCode}`}
        >
          <p className="text-warning">
            ${roundedRate.toFixed(2)}/hr is well outside the standard $
            {needsConfirm.low.toFixed(2)}
            {needsConfirm.high !== needsConfirm.low ? `–$${needsConfirm.high.toFixed(2)}` : ''}/hr
            for {roleCode}. Set it anyway?
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ payRateOverride: roundedRate, confirmed: true })}
              data-testid={`pay-override-confirm-save-${roleCode}`}
            >
              Yes, set ${roundedRate.toFixed(2)}/hr
            </Button>
            <Button
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => setNeedsConfirm(null)}
              data-testid={`pay-override-confirm-cancel-${roleCode}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {showError && (
        <p className="mt-2 text-sm text-danger" data-testid={`pay-override-error-${roleCode}`}>
          {errorText}
        </p>
      )}
    </div>
  );
}

function SkillsCard({ data }: { data: AdminCaregiverDetailResponse }) {
  const queryClient = useQueryClient();
  const eligible = skillsForRoles(data.roles.map((r) => r.code));
  const eligibleCodes = new Set(eligible.map((s) => s.code));
  const held = new Set(data.skills.filter((s) => eligibleCodes.has(s)));

  const setSkills = useMutation({
    mutationFn: async (skillCodes: string[]) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/skills', {
          params: { path: { id: data.id } },
          body: { skillCodes },
        } as never),
      )) as unknown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers', data.id] });
    },
  });

  function toggle(code: string): void {
    const next = new Set(held);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSkills.mutate([...next]);
  }

  const showError =
    setSkills.isError && !(setSkills.error instanceof TotpStepUpRequiredError);

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-skills-card">
      <CardHeader
        title="Skills"
        description="Self-declared, role-scoped experience. Editing requires a fresh 2FA step-up."
      />
      {eligible.length === 0 ? (
        <p className="text-sm text-ink-muted">
          No skills apply to this caregiver’s roles (
          {data.roles.map((r) => r.code).join(', ') || 'none'}).
        </p>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          {eligible.map((s) => (
            <label
              key={s.code}
              className="flex items-center gap-2 text-sm text-forest"
            >
              <input
                type="checkbox"
                checked={held.has(s.code)}
                disabled={setSkills.isPending}
                onChange={() => toggle(s.code)}
                data-testid={`admin-skill-${s.code}`}
              />
              {s.label}
            </label>
          ))}
        </div>
      )}
      {showError && (
        <p className="mt-2 text-sm text-danger">
          Couldn’t save skills. Please try again.
        </p>
      )}
    </Card>
  );
}

// Operator view+edit of a caregiver's voluntary immunization declaration
// (2026-08 vaccine revamp). Mirrors SkillsCard but NOT role-scoped — the full
// IMMUNIZATION_CATALOG renders for every caregiver. Full-set / idempotent POST;
// TotpFresh-guarded like the sibling skills write.
function ImmunizationsCard({ data }: { data: AdminCaregiverDetailResponse }) {
  const queryClient = useQueryClient();
  const held = new Set(data.immunizations);

  const setImmunizations = useMutation({
    mutationFn: async (immunizationCodes: string[]) =>
      (await unwrap(
        api.POST('/api/v1/admin/caregivers/{id}/immunizations', {
          params: { path: { id: data.id } },
          body: { immunizationCodes },
        } as never),
      )) as unknown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers', data.id] });
    },
  });

  function toggle(code: string): void {
    const next = new Set(held);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setImmunizations.mutate([...next]);
  }

  const showError =
    setImmunizations.isError &&
    !(setImmunizations.error instanceof TotpStepUpRequiredError);

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-immunizations-card">
      <CardHeader
        title="Immunizations"
        description="Voluntary self-declared immunizations. Not required and not expiry-tracked. Editing requires a fresh 2FA step-up."
      />
      <div className="mt-2 flex flex-col gap-2">
        {IMMUNIZATION_CATALOG.map((i) => (
          <label key={i.code} className="flex items-center gap-2 text-sm text-forest">
            <input
              type="checkbox"
              checked={held.has(i.code)}
              disabled={setImmunizations.isPending}
              onChange={() => toggle(i.code)}
              data-testid={`admin-immunization-${i.code}`}
            />
            {i.label}
          </label>
        ))}
      </div>
      {showError && (
        <p className="mt-2 text-sm text-danger">
          Couldn’t save immunizations. Please try again.
        </p>
      )}
    </Card>
  );
}

/** Doc 16 — optional additional certificates the care professional uploaded
 *  (WHMIS, safe lifts, GPA, … or Other). Not reviewed and never gating; the
 *  operator views the file (e.g. to send a facility proof) and can remove a
 *  wrong upload. Removing needs a fresh 2FA step-up. */
function AdditionalCertificatesCard({ data }: { data: AdminCaregiverDetailResponse }) {
  const queryClient = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // `?? []`: during a deploy the new admin app can briefly talk to an API that
  // predates the field — show "None added" rather than crash the page.
  const certificates = data.additionalCertificates ?? [];

  const remove = useMutation({
    mutationFn: async (certificateId: string) =>
      (await unwrap(
        api.DELETE('/api/v1/admin/caregivers/{id}/certificates/{certificateId}', {
          params: { path: { id: data.id, certificateId } },
        } as never),
      )) as unknown,
    onSuccess: () => {
      setConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers', data.id] });
    },
    // e.g. 404 — the care professional already removed it: refresh so the stale
    // row disappears instead of failing on every retry.
    onError: (err) => {
      if (err instanceof TotpStepUpRequiredError) return;
      setConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'caregivers', data.id] });
    },
  });

  const showError = remove.isError && !(remove.error instanceof TotpStepUpRequiredError);

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-certificates-card">
      <CardHeader
        title={`Additional certificates (${certificates.length})`}
        description="Optional, self-reported by the care professional — not reviewed and never required. Facilities see listed certificates by name + expiry on booked shifts, and “Other” ones only as a count (the typed name stays here); open the document to send proof on request."
      />
      {certificates.length === 0 ? (
        <p className="text-sm text-ink-muted">None added.</p>
      ) : (
        <ul className="divide-y divide-border">
          {certificates.map((cert) => (
            <li key={cert.id} className="py-3" data-testid={`caregiver-certificate-${cert.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="font-semibold text-ink [overflow-wrap:anywhere]">
                    {cert.displayName}
                    {cert.isExpired && (
                      <span className="ml-2 text-xs font-semibold text-danger">EXPIRED</span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {[
                      cert.issuedAt ? `Issued ${cert.issuedAt}` : null,
                      cert.expiresAt
                        ? `${cert.isExpired ? 'Expired' : 'Expires'} ${cert.expiresAt}`
                        : 'No expiry date',
                      // uploadedAt is an instant — format in Eastern time, not a UTC slice.
                      `Uploaded ${new Date(cert.uploadedAt).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {cert.downloadUrl && (
                    <a
                      href={cert.downloadUrl}
                      target="_blank"
                      rel="noopener"
                      className="text-xs text-forest underline"
                      data-testid={`certificate-${cert.id}-download`}
                    >
                      View document
                    </a>
                  )}
                  {confirmId === cert.id ? (
                    <>
                      <button
                        // a11y — focus moves here when the confirm pair appears.
                        autoFocus
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(cert.id)}
                        aria-label={`Confirm remove ${cert.displayName}`}
                        className="rounded-md border border-danger px-2 py-0.5 text-xs text-danger disabled:opacity-50"
                        data-testid={`certificate-${cert.id}-remove-confirm`}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmId(null);
                          // Return focus to this row's Remove (re-rendered next tick).
                          requestAnimationFrame(() =>
                            document
                              .querySelector<HTMLButtonElement>(
                                `[data-testid="certificate-${cert.id}-remove"]`,
                              )
                              ?.focus(),
                          );
                        }}
                        aria-label={`Keep ${cert.displayName}`}
                        className="text-xs text-ink-muted underline"
                      >
                        Keep
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(cert.id)}
                      aria-label={`Remove ${cert.displayName}`}
                      className="text-xs text-ink-muted underline hover:text-danger"
                      data-testid={`certificate-${cert.id}-remove`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {showError && (
        <p className="mt-2 text-sm text-danger" data-testid="caregiver-certificates-error">
          {messageOf(remove.error, 'Couldn’t remove the certificate. Please try again.')}
        </p>
      )}
    </Card>
  );
}

function StatusActionsCard(props: {
  status: string;
  suspendedReason: string | null;
  onApprove: () => Promise<unknown>;
  onUnsuspend: () => Promise<unknown>;
  onSuspend: (reason: string) => Promise<unknown>;
  onTerminate: (reason: string) => Promise<unknown>;
  onReject: (notes: string) => Promise<unknown>;
  isApprovePending: boolean;
  isUnsuspendPending: boolean;
  isSuspendPending: boolean;
  isTerminatePending: boolean;
  isRejectPending: boolean;
}) {
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  // H-F2 — surface failures inline; preserve typed reasons on failure so
  // a 409 (multi-operator race) / 5xx doesn't silently clear the field.
  const [statusError, setStatusError] = useState<string | null>(null);
  // Suspend + Terminate both route through a confirmation modal that owns its own
  // reason + inline error (modal stays open on a 409/5xx). Terminate is
  // irreversible → typed-token <DangerButton>; Suspend is reversible →
  // reason-only <OverrideActionButton>. No local reason/error state needed here.

  async function runStatus(action: () => Promise<unknown>): Promise<void> {
    setStatusError(null);
    try {
      await action();
    } catch (e) {
      const msg = actionErrorMessage(e);
      if (msg) setStatusError(msg);
    }
  }

  const isActive = props.status === 'ACTIVE';
  const isSuspended = props.status === 'SUSPENDED';
  const isTerminated = props.status === 'TERMINATED';
  const isOnboarding = props.status === 'ONBOARDING';
  // Phase 5g — Approve is the vetting gate (UNDER_REVIEW → ONBOARDING). Only
  // UNDER_REVIEW is approvable; an APPLYING caregiver hasn't submitted yet.
  // There is no manual Activate: ACTIVE auto-flips once the care professional
  // completes banking (verified) + SIN + contract. SUSPENDED → ACTIVE uses the
  // dedicated Unsuspend mutation.
  const canApprove = props.status === 'UNDER_REVIEW';

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-status-card">
      <CardHeader title="Status" />

      <p
        className="mb-4 flex items-center gap-2 text-sm text-ink-muted"
        data-testid="caregiver-status-current"
      >
        Current status:{' '}
        <StatusBadge status={props.status} testId="caregiver-status-card-badge" />
      </p>

      {isSuspended && props.suspendedReason && (
        <p className="mb-4 rounded-md bg-gold/10 px-3 py-2 text-sm text-gold">
          <strong>Suspended:</strong> {props.suspendedReason}
        </p>
      )}

      {isOnboarding && (
        <p className="mb-4 rounded-md bg-forest/10 px-3 py-2 text-sm text-forest" data-testid="caregiver-onboarding-note">
          <strong>Onboarding:</strong> vetting approved. Activates automatically
          once banking is verified and SIN + contract are on file. Verify their
          banking in the Banking section below to complete activation.
        </p>
      )}

      {statusError && (
        <p
          className="mb-4 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
          data-testid="caregiver-status-error"
        >
          {statusError}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {canApprove && (
          <div>
            <Button
              size="sm"
              variant="primary"
              loading={props.isApprovePending}
              onClick={() => void runStatus(props.onApprove)}
              data-testid="caregiver-approve-button"
            >
              Approve
            </Button>
            <p className="mt-1 text-xs text-ink-muted">
              Confirms vetting complete → moves to onboarding (banking + SIN +
              contract). Activation is automatic once those are done.
            </p>
          </div>
        )}

        {/* Audit H8 — the reject endpoint (UNDER_REVIEW → APPLYING) existed with
            zero admin UI, so an operator rejecting a blurry doc had to curl it;
            worse, the caregiver's portal hides credential/compliance uploads
            while UNDER_REVIEW, so this send-back is the ONLY path that lets them
            self-serve the fix. */}
        {canApprove && (
          <form
            className="flex flex-col gap-2 border-l-4 border-gold pl-3"
            onSubmit={(e) => {
              e.preventDefault();
              const notes = rejectNotes.trim();
              if (notes.length === 0) return;
              setRejectError(null);
              props.onReject(notes).then(
                () => setRejectNotes(''),
                (err: unknown) => {
                  const msg = actionErrorMessage(err);
                  if (msg) setRejectError(msg);
                },
              );
            }}
          >
            <Field label="Send back to applicant — what's missing/wrong (shown to the caregiver)">
              <Input
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="e.g. VSC scan is blurry — please re-upload a clear copy"
                data-testid="caregiver-reject-notes"
              />
            </Field>
            {rejectError && (
              <p className="text-sm text-danger" data-testid="caregiver-reject-error">
                {rejectError}
              </p>
            )}
            <div>
              <Button
                type="submit"
                size="sm"
                variant="secondary"
                disabled={rejectNotes.trim().length === 0}
                loading={props.isRejectPending}
                data-testid="caregiver-reject-button"
              >
                Send back to applicant
              </Button>
            </div>
          </form>
        )}

        {isSuspended && (
          <div>
            <Button
              size="sm"
              variant="primary"
              loading={props.isUnsuspendPending}
              onClick={() => void runStatus(props.onUnsuspend)}
              data-testid="caregiver-unsuspend-button"
            >
              Unsuspend
            </Button>
            <p className="mt-1 text-xs text-ink-muted">
              Resumes a previously-vetted care professional. Original
              activation date is preserved.
            </p>
          </div>
        )}

        {isActive && (
          <div className="border-l-4 border-gold pl-3">
            <OverrideActionButton
              actionLabel="Suspend professional"
              reasonLabel="Suspension reason (operator-only)"
              onConfirm={async ({ reason }) => {
                await props.onSuspend(reason);
              }}
              loading={props.isSuspendPending}
              data-testid="caregiver-suspend-button"
              instructions={
                <>
                  Pauses this professional — they stop receiving offers.
                  Reversible via Unsuspend; the original activation date is kept.
                </>
              }
            >
              Suspend professional
            </OverrideActionButton>
            <p className="mt-1 text-xs text-ink-muted">
              Pauses offers; reversible. Reason is recorded.
            </p>
          </div>
        )}

        {/* Terminate is irreversible + the highest-blast-radius action on this
            page, so it carries the strongest guardrail: the same typed-token
            <DangerButton> modal as Void / Force-close (type "TERMINATE" + a
            reason ≥10 chars), not a bare reason field + red button. */}
        {!isTerminated && (
          <div className="border-l-4 border-danger pl-3">
            <DangerButton
              entityName="TERMINATE"
              confirmationToken="TERMINATE"
              actionLabel="Terminate professional"
              onConfirm={async ({ reason }) => {
                await props.onTerminate(reason ?? '');
              }}
              loading={props.isTerminatePending}
              data-testid="caregiver-terminate-button"
            >
              Terminate professional
            </DangerButton>
            <p className="mt-1 text-xs text-ink-muted">
              Irreversible — ends the working relationship. Reason is recorded.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function CredentialsCard(props: {
  credentials: AdminCredentialDetail[];
  onApprove: (credId: string) => Promise<unknown>;
  onReject: (credId: string, notes: string) => Promise<unknown>;
  isApprovePending: boolean;
  isRejectPending: boolean;
}) {
  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-credentials-card">
      <CardHeader
        title={`Credentials (${props.credentials.length})`}
        description="NACC / CNO / VSC / Government ID. Approve when scan + reference match; reject with actionable notes."
      />
      {props.credentials.length === 0 ? (
        <p className="text-sm text-ink-muted">No credentials uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {props.credentials.map((cred) => (
            <ArtifactReviewRow
              key={cred.id}
              testId={`caregiver-credential-${cred.id}`}
              title={CREDENTIAL_LABELS[cred.type]}
              status={cred.status}
              referenceLabel="Reference"
              referenceValue={cred.externalReferenceNumber}
              downloadUrl={cred.downloadUrl}
              notes={cred.notes}
              onApprove={() => props.onApprove(cred.id)}
              onReject={(notes) => props.onReject(cred.id, notes)}
              isApprovePending={props.isApprovePending}
              isRejectPending={props.isRejectPending}
              testKey={`credential-${cred.id}`}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ComplianceCard(props: {
  artifacts: AdminComplianceArtifactDetail[];
  onApprove: (artifactId: string) => Promise<unknown>;
  onReject: (artifactId: string, notes: string) => Promise<unknown>;
  isApprovePending: boolean;
  isRejectPending: boolean;
}) {
  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-compliance-card">
      <CardHeader
        title={`Compliance artifacts (${props.artifacts.length})`}
        description="TB test (required) and General Liability insurance (optional) — renewable, expiry-tracked. WSIB moved to the contract/onboarding stage; vaccination is no longer collected (voluntary immunizations live on the profile)."
      />
      {props.artifacts.length === 0 ? (
        <p className="text-sm text-ink-muted">
          No compliance artifacts uploaded yet.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {props.artifacts.map((art) => (
            <ArtifactReviewRow
              key={art.id}
              testId={`caregiver-compliance-${art.id}`}
              title={COMPLIANCE_LABELS[art.type]}
              status={art.status}
              referenceLabel="Expires"
              referenceValue={art.expiresAt}
              downloadUrl={art.downloadUrl}
              notes={art.notes}
              onApprove={() => props.onApprove(art.id)}
              onReject={(notes) => props.onReject(art.id, notes)}
              isApprovePending={props.isApprovePending}
              isRejectPending={props.isRejectPending}
              testKey={`compliance-${art.id}`}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Shared review row for credentials + compliance artifacts. */
function ArtifactReviewRow(props: {
  testId: string;
  title: string;
  status: string;
  referenceLabel: string;
  referenceValue: string | null;
  downloadUrl: string | null;
  notes: string | null;
  onApprove: () => Promise<unknown>;
  onReject: (notes: string) => Promise<unknown>;
  isApprovePending: boolean;
  isRejectPending: boolean;
  testKey: string;
}) {
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  // H-F2 — surface failures inline; keep the typed notes + reject form
  // open on failure so the operator can retry without re-typing.
  const [error, setError] = useState<string | null>(null);

  const canReview = props.status === 'PENDING' || props.status === 'SUBMITTED';

  return (
    <li className="py-4" data-testid={props.testId}>
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-semibold text-ink">{props.title}</p>
          {props.referenceValue && (
            <p className="mt-0.5 text-xs text-ink-muted">
              {props.referenceLabel}: {props.referenceValue}
            </p>
          )}
        </div>
        <span
          className={`text-xs font-semibold ${
            props.status === 'APPROVED'
              ? 'text-forest'
              : props.status === 'REJECTED'
                ? 'text-danger'
                : 'text-ink-muted'
          }`}
        >
          {props.status}
        </span>
      </div>

      {props.notes && (
        <p className="mt-2 rounded-md bg-cream/50 px-2 py-1 text-xs text-ink-muted">
          Notes: {props.notes}
        </p>
      )}

      {props.downloadUrl && (
        <a
          href={props.downloadUrl}
          target="_blank"
          rel="noopener"
          className="mt-2 inline-block text-xs text-forest underline"
          data-testid={`${props.testKey}-download`}
        >
          View scan
        </a>
      )}

      {error && (
        <p className="mt-2 text-sm text-danger" data-testid={`${props.testKey}-error`}>
          {error}
        </p>
      )}

      {canReview && !rejectMode && (
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={props.isApprovePending}
            onClick={() => {
              setError(null);
              props.onApprove().catch((e: unknown) => {
                const msg = actionErrorMessage(e);
                if (msg) setError(msg);
              });
            }}
            data-testid={`${props.testKey}-approve`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRejectMode(true)}
            data-testid={`${props.testKey}-reject-open`}
          >
            Reject…
          </Button>
        </div>
      )}

      {canReview && rejectMode && (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const notes = rejectNotes.trim();
            if (notes.length === 0) return;
            setError(null);
            props.onReject(notes).then(
              () => {
                setRejectNotes('');
                setRejectMode(false);
              },
              (err: unknown) => {
                const msg = actionErrorMessage(err);
                if (msg) setError(msg);
              },
            );
          }}
        >
          <Field label="Rejection notes (visible to professional)">
            <Input
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="e.g. scan is blurry; please re-upload"
              data-testid={`${props.testKey}-reject-notes`}
            />
          </Field>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              variant="danger"
              disabled={rejectNotes.trim().length === 0}
              loading={props.isRejectPending}
              data-testid={`${props.testKey}-reject-confirm`}
            >
              Confirm reject
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejectMode(false);
                setRejectNotes('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}

function BankingCard(props: {
  eftOnFile: boolean;
  eftVerified: boolean;
  eftRejected: boolean;
  eftRejectionReason: string | null;
  bankingAccountLast4: string | null;
  taxEntityType: 'INDIVIDUAL' | 'CORPORATION';
  sinOnFile: boolean;
  businessNumber: string | null;
  hstRegistered: boolean;
  hstNumber: string | null;
  onVerify: () => Promise<unknown>;
  onReject: (reason: string) => Promise<unknown>;
  /** Audit H4 — fetch the void-cheque URL from the TotpFresh /banking endpoint
   *  on demand (may trigger the 2FA step-up modal). Returns null if none on file. */
  onRevealVoidCheque: () => Promise<string | null>;
  onSetPayoutFrequency: (input: {
    frequency: 'BIWEEKLY' | 'WEEKLY';
    reason: string;
  }) => Promise<unknown>;
  isVerifyPending: boolean;
  isRejectPending: boolean;
  isRevealPending: boolean;
  isFrequencyPending: boolean;
}) {
  const [rejectReason, setRejectReason] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  // Audit H4 — the signed void-cheque URL, once revealed via the 2FA-gated fetch.
  const [voidChequeUrl, setVoidChequeUrl] = useState<string | null>(null);
  // 14b-2 post-audit-round-2 — operator-typed reason required for
  // payout-frequency flip per the 11-2 lock (min 10 chars). Previously
  // the buttons fired with no reason input + wrong body key, hitting
  // 400 on every click.
  const [frequencyReason, setFrequencyReason] = useState('');
  // H-F2 — surface failures inline; preserve typed input on failure.
  const [bankingError, setBankingError] = useState<string | null>(null);
  const [frequencyError, setFrequencyError] = useState<string | null>(null);
  // A "Revoke" on a verified account reuses the reject endpoint (Option A), so the
  // same reason form serves both — only the labels differ.

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-banking-card">
      <CardHeader
        title="Banking & payout"
        description="Operator verifies EFT info against the void cheque before it's eligible for payouts. Decrypted operator view at GET /admin/caregivers/:id/banking."
      />

      <dl className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row
          label="Void cheque"
          value={props.eftOnFile ? 'Submitted' : 'Not submitted'}
        />
        <Row
          label="Verification"
          value={
            props.eftVerified
              ? 'Verified'
              : props.eftRejected
                ? 'Rejected'
                : props.eftOnFile
                  ? 'Awaiting verification'
                  : 'Not submitted'
          }
        />
        {props.bankingAccountLast4 && (
          <Row label="Account" value={`••••${props.bankingAccountLast4}`} />
        )}
        <Row
          label="Tax identity"
          value={
            props.taxEntityType === 'CORPORATION'
              ? `Corporation — BN ${props.businessNumber ?? 'not on file'}`
              : `Individual — SIN ${props.sinOnFile ? 'on file' : 'not on file'}`
          }
        />
        <Row
          label="HST registered"
          value={
            props.hstRegistered
              ? `Yes${props.hstNumber ? ` (${props.hstNumber})` : ''}`
              : 'No'
          }
        />
      </dl>

      {/* Audit H4 — the void cheque (raw banking PII) is fetched on demand from
          the TotpFresh /banking endpoint; the reveal may pop the 2FA step-up. */}
      {props.eftOnFile &&
        (voidChequeUrl ? (
          <a
            href={voidChequeUrl}
            target="_blank"
            rel="noopener"
            className="mb-3 inline-block text-sm text-forest underline"
            data-testid="caregiver-banking-void-cheque-link"
          >
            Open void cheque
          </a>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            loading={props.isRevealPending}
            className="mb-3"
            data-testid="caregiver-banking-void-cheque-reveal"
            onClick={async () => {
              setBankingError(null);
              try {
                const url = await props.onRevealVoidCheque();
                setVoidChequeUrl(url);
                if (!url) setBankingError('No void cheque is on file for this caregiver.');
              } catch (err) {
                if (err instanceof TotpStepUpRequiredError) return; // modal handles it
                setBankingError(messageOf(err) ?? 'Could not load the void cheque.');
              }
            }}
          >
            View void cheque (2FA)
          </Button>
        ))}

      {bankingError && (
        <p
          className="mb-2 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
          data-testid="caregiver-banking-error"
        >
          {bankingError}
        </p>
      )}

      {/* Awaiting verification → verify or reject. */}
      {props.eftOnFile && !props.eftVerified && !props.eftRejected && !rejectMode && (
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={props.isVerifyPending}
            onClick={() => {
              setBankingError(null);
              props.onVerify().catch((e: unknown) => {
                const msg = actionErrorMessage(e);
                if (msg) setBankingError(msg);
              });
            }}
            data-testid="caregiver-banking-verify-button"
          >
            Verify banking
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRejectMode(true)}
            data-testid="caregiver-banking-reject-open"
          >
            Reject…
          </Button>
        </div>
      )}

      {/* Verified → terminal from the operator side. There is deliberately no
          "revoke": per 11b-6 verified banking stays active; the professional
          changes it by re-submitting (PATCH /me/banking), which resets
          verification back into this queue. Status only, no action button.
          (An earlier build offered a Revoke button wired to the reject endpoint,
          but reject leaves eftVerifiedAt set — so it was a silent no-op.) */}
      {props.eftVerified && (
        <div className="mt-2">
          <span
            className="text-sm font-medium text-success"
            data-testid="caregiver-banking-verified"
          >
            ✓ Verified — eligible for payouts.
          </span>
          <p className="mt-1 text-xs text-ink-muted">
            To change these details, the professional re-submits their banking,
            which returns here for re-verification.
          </p>
        </div>
      )}

      {/* Rejected → nothing for the operator to do; the professional re-submits. */}
      {props.eftRejected && !rejectMode && (
        <p className="mt-2 text-sm text-ink-muted" data-testid="caregiver-banking-rejected">
          Rejected{props.eftRejectionReason ? ` — ${props.eftRejectionReason}` : ''}. The
          professional needs to re-submit their banking.
        </p>
      )}

      {props.eftOnFile && rejectMode && (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const reason = rejectReason.trim();
            if (reason.length < 10) return;
            setBankingError(null);
            props.onReject(reason).then(
              () => {
                setRejectReason('');
                setRejectMode(false);
              },
              (err: unknown) => {
                const msg = actionErrorMessage(err);
                if (msg) setBankingError(msg);
              },
            );
          }}
        >
          <Field
            label="Rejection reason (≥10 chars, visible to professional)"
          >
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. void cheque illegible; please re-upload"
              data-testid="caregiver-banking-reject-reason"
            />
          </Field>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              variant="danger"
              disabled={rejectReason.trim().length < 10}
              loading={props.isRejectPending}
              data-testid="caregiver-banking-reject-confirm"
            >
              Confirm reject
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejectMode(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="mt-6 border-t border-border pt-4">
        <p className="mb-2 text-sm font-semibold text-ink">Payout frequency</p>
        <Field label="Reason (operator-only; required for the change-log per 11-2)">
          <Input
            value={frequencyReason}
            onChange={(e) => setFrequencyReason(e.target.value)}
            placeholder="e.g. professional requested weekly cadence"
            data-testid="caregiver-payout-frequency-reason"
          />
        </Field>
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={frequencyReason.trim().length < 10}
            loading={props.isFrequencyPending}
            onClick={() => {
              setFrequencyError(null);
              props
                .onSetPayoutFrequency({
                  frequency: 'BIWEEKLY',
                  reason: frequencyReason.trim(),
                })
                .then(
                  () => setFrequencyReason(''),
                  (err: unknown) => {
                    const msg = actionErrorMessage(err);
                    if (msg) setFrequencyError(msg);
                  },
                );
            }}
            data-testid="caregiver-payout-frequency-biweekly"
          >
            Set BIWEEKLY
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={frequencyReason.trim().length < 10}
            loading={props.isFrequencyPending}
            onClick={() => {
              setFrequencyError(null);
              props
                .onSetPayoutFrequency({
                  frequency: 'WEEKLY',
                  reason: frequencyReason.trim(),
                })
                .then(
                  () => setFrequencyReason(''),
                  (err: unknown) => {
                    const msg = actionErrorMessage(err);
                    if (msg) setFrequencyError(msg);
                  },
                );
            }}
            data-testid="caregiver-payout-frequency-weekly"
          >
            Set WEEKLY
          </Button>
        </div>
        {frequencyError && (
          <p className="mt-1 text-sm text-danger" data-testid="caregiver-payout-frequency-error">
            {frequencyError}
          </p>
        )}
        <p className="mt-1 text-xs text-ink-muted">
          BIWEEKLY is Q9 default; WEEKLY is the per-professional override.
        </p>
      </div>
    </Card>
  );
}

/**
 * Phase 17 (change-list 1.2) — read-only work history + résumé, for vetting.
 *
 * The care professional owns these rows (they add/edit them in the portal);
 * the operator only reads them. Buyers never see any of this — they see only
 * the derived `totalYearsExperience` on the shift-detail profile.
 */
function WorkExperienceCard({ caregiverId }: { caregiverId: string }) {
  const workQuery = useQuery<WorkExperienceListResponse>({
    queryKey: ['admin', 'caregiver', caregiverId, 'work-experience'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}/work-experience', {
          params: { path: { id: caregiverId } },
        } as never),
      )) as unknown as WorkExperienceListResponse,
    retry: false,
  });

  const resumeQuery = useQuery<CaregiverResumeResponse>({
    queryKey: ['admin', 'caregiver', caregiverId, 'resume'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}/resume', {
          params: { path: { id: caregiverId } },
        } as never),
      )) as unknown as CaregiverResumeResponse,
    retry: false,
  });

  const entries = workQuery.data?.items ?? [];
  const totalYears = workQuery.data?.totalYearsExperience ?? 0;
  const resume = resumeQuery.data;

  return (
    <Card padding="lg" className="mt-6" data-testid="admin-work-experience-card">
      <CardHeader
        title="Work experience"
        description="Self-entered career history + résumé. Vetting aid — facilities see only the total years."
      />

      {workQuery.isPending && (
        <p className="mt-3 font-body text-sm text-ink-muted">Loading…</p>
      )}

      {workQuery.isError && (
        <p className="mt-3 font-body text-sm text-danger">Couldn't load work history.</p>
      )}

      {workQuery.data !== undefined && (
        <>
          <p className="mt-3 font-body text-sm text-ink" data-testid="admin-work-total">
            <strong>
              {totalYears > 0
                ? `${String(totalYears)} ${totalYears === 1 ? 'year' : 'years'} of experience`
                : 'Less than a year of experience'}
            </strong>
            <span className="text-ink-muted">
              {' '}
              (derived — concurrent roles counted once)
            </span>
          </p>

          {entries.length === 0 ? (
            <p className="mt-3 font-body text-sm text-ink-muted">
              No work history entered.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[36rem] text-left font-body text-sm">
                <thead className="border-b border-border text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Employer</th>
                    <th className="py-2 pr-4">Service length</th>
                    <th className="py-2">Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-border last:border-b-0">
                      <td className="py-2 pr-4 font-medium text-forest">{e.roleTitle}</td>
                      <td className="py-2 pr-4">{e.employer}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatAdminRange(e.startedOn, e.endedOn)}
                      </td>
                      <td className="py-2 text-ink-muted">{e.comments ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="mt-5 border-t border-border pt-4">
        <p className="font-body text-sm font-medium text-forest">Résumé</p>
        {resumeQuery.isPending ? (
          <p className="mt-1 font-body text-sm text-ink-muted">Loading…</p>
        ) : resumeQuery.isError ? (
          // audit medium — a failed fetch must NOT masquerade as "No résumé on
          // file" (an operator might reject/stall a caregiver who did upload one).
          <p className="mt-1 font-body text-sm text-danger" data-testid="admin-resume-error">
            Couldn&apos;t load résumé — try refreshing.
          </p>
        ) : resume?.url != null ? (
          <a
            href={resume.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block font-body text-sm text-forest underline"
            data-testid="admin-resume-download"
          >
            {resume.filename ?? 'Download résumé'}
          </a>
        ) : (
          <p className="mt-1 font-body text-sm text-ink-muted">No résumé on file.</p>
        )}
      </div>
    </Card>
  );
}

// ── Availability (operator read-only) ──────────────────────────────────────
// Mirrors the caregiver's own portal rendering (described-window list, WEEKLY
// first then ONE_OFF). Read-only: availability is caregiver-owned (Principle 9).
// The describe/friendlyTz/sort helpers are small pure ports of the portal's
// CaregiverAvailabilitySection (separate apps — no shared component).

const AVAIL_DAY_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

function availFriendlyTz(iana: string | null | undefined): string {
  if (!iana) return '';
  const map: Record<string, string> = {
    'America/Toronto': 'Eastern',
    'America/Nipigon': 'Eastern',
    'America/Thunder_Bay': 'Eastern',
    'America/Atikokan': 'Eastern (no DST)',
  };
  return map[iana] ?? iana;
}

function describeAvailability(block: AvailabilityResponse): string {
  if (block.recurrenceType === 'WEEKLY') {
    const day = block.weeklyDayOfWeek ? (AVAIL_DAY_LABELS[block.weeklyDayOfWeek] ?? '?') : '?';
    const start = block.weeklyStartLocalTime ?? '?';
    const end = block.weeklyEndLocalTime ?? '?';
    const tz = availFriendlyTz(block.weeklyTimezone) || '?';
    const friendlyDate = (iso: string): string =>
      new Date(`${iso}T00:00:00`).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    const range =
      block.weeklyEffectiveFrom || block.weeklyEffectiveTo
        ? ` (${block.weeklyEffectiveFrom ? friendlyDate(block.weeklyEffectiveFrom) : 'now'} – ${
            block.weeklyEffectiveTo ? friendlyDate(block.weeklyEffectiveTo) : 'ongoing'
          })`
        : '';
    return `${day}s, ${start}–${end} ${tz}${range}`;
  }
  const start = block.startUtc ? new Date(block.startUtc) : null;
  const end = block.endUtc ? new Date(block.endUtc) : null;
  if (!start || !end) return 'One-time block (date unset)';
  const fmt = (d: Date): string =>
    d.toLocaleString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

function sortAvailability(blocks: AvailabilityResponse[]): AvailabilityResponse[] {
  return [...blocks].sort((a, b) => {
    if (a.recurrenceType !== b.recurrenceType) {
      return a.recurrenceType === 'WEEKLY' ? -1 : 1;
    }
    if (a.recurrenceType === 'WEEKLY' && b.recurrenceType === 'WEEKLY') {
      const dayDiff = (a.weeklyDayOfWeek ?? 0) - (b.weeklyDayOfWeek ?? 0);
      if (dayDiff !== 0) return dayDiff;
      return (a.weeklyStartLocalTime ?? '').localeCompare(b.weeklyStartLocalTime ?? '');
    }
    return (a.startUtc ?? '').localeCompare(b.startUtc ?? '');
  });
}

function AvailabilityCard({ caregiverId }: { caregiverId: string }) {
  const availQuery = useQuery<AvailabilityListResponse>({
    queryKey: ['admin', 'caregiver', caregiverId, 'availability'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers/{id}/availability', {
          params: { path: { id: caregiverId } },
        } as never),
      )) as unknown as AvailabilityListResponse,
    retry: false,
  });

  const blocks = sortAvailability(availQuery.data?.items ?? []);
  const weekly = blocks.filter((b) => b.recurrenceType === 'WEEKLY');
  const oneOff = blocks.filter((b) => b.recurrenceType === 'ONE_OFF');

  return (
    <Card padding="lg" className="mt-6" data-testid="caregiver-availability-card">
      <CardHeader
        title={`Availability (${String(blocks.length)})`}
        description="When the professional says they're available — the same windows matching reads. Read-only; the professional manages these in their own portal."
      />

      {availQuery.isPending && (
        <p className="mt-3 font-body text-sm text-ink-muted">Loading…</p>
      )}

      {availQuery.isError && (
        // A failed fetch must NOT masquerade as "no availability set" — an operator
        // could misjudge a caregiver who actually has windows on file.
        <p className="mt-3 font-body text-sm text-danger" data-testid="caregiver-availability-error">
          Couldn&apos;t load availability — try refreshing.
        </p>
      )}

      {availQuery.data !== undefined && blocks.length === 0 && (
        <p className="mt-3 font-body text-sm text-ink-muted" data-testid="caregiver-availability-empty">
          No availability set. This professional won&apos;t be auto-matched to any shifts until they
          add availability in their portal (you can still manually target them).
        </p>
      )}

      {availQuery.data !== undefined && blocks.length > 0 && (
        <div className="mt-3 flex flex-col gap-4">
          {weekly.length > 0 && (
            <div>
              <p className="font-body text-xs font-semibold uppercase text-ink-muted">Recurring weekly</p>
              <ul className="mt-2 flex flex-col gap-1" data-testid="caregiver-availability-weekly">
                {weekly.map((b) => (
                  <li key={b.id} className="font-body text-sm text-ink">
                    {describeAvailability(b)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {oneOff.length > 0 && (
            <div>
              <p className="font-body text-xs font-semibold uppercase text-ink-muted">One-time</p>
              <ul className="mt-2 flex flex-col gap-1" data-testid="caregiver-availability-one-off">
                {oneOff.map((b) => (
                  <li key={b.id} className="font-body text-sm text-ink">
                    {describeAvailability(b)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** `2020-03` + `2022-01` → `Mar 2020 – Jan 2022` (null end = Current). */
function formatAdminRange(startedOn: string, endedOn: string | null): string {
  const fmt = (month: string): string => {
    const [y, m] = month.split('-');
    if (y === undefined || m === undefined) return month;
    return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleDateString('en-CA', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };
  return `${fmt(startedOn)} – ${endedOn === null ? 'Current' : fmt(endedOn)}`;
}
